/**
 * OfferScoreGewichteCard — the Ü7 Offer-Score weights editor on the Regelwerk admin page (P2 Placement
 * Slice 11). Seven inputs, a sum check, Speichern (regelwerk.manage) + reset to the code default.
 */

import { useEffect, useState } from 'react';
import {
  gewichteSumme,
  OFFER_SCORE_DIMENSION_LABELS,
  OFFER_SCORE_DIMENSION_VALUES,
  sindGewichteGueltig,
  VERGLEICH_GEWICHTE_SUMME_HINWEIS,
  type OfferScoreGewichte,
} from '@brokly/shared';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { dateShortDe } from '../../../lib/format';
import {
  useOfferScoreGewichte,
  usePutOfferScoreGewichte,
  useResetOfferScoreGewichte,
} from '../../placements/usePlacementVergleich';

export function OfferScoreGewichteCard({ canManage }: { canManage: boolean }) {
  const { data } = useOfferScoreGewichte();
  const put = usePutOfferScoreGewichte();
  const reset = useResetOfferScoreGewichte();
  const [draft, setDraft] = useState<OfferScoreGewichte | null>(null);

  useEffect(() => {
    if (data) setDraft(data.gewichte);
  }, [data]);

  if (!data || !draft) return null;
  const summe = gewichteSumme(draft);
  const gueltig = sindGewichteGueltig(draft);

  const set = (dim: keyof OfferScoreGewichte, v: string): void => {
    const n = v === '' ? 0 : Math.trunc(Number(v));
    setDraft((d) => (d ? { ...d, [dim]: Number.isFinite(n) ? n : 0 } : d));
  };

  return (
    <section className="mt-8 rounded-lg border border-border p-4" data-testid="offer-score-gewichte-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-medium">Offer-Score-Gewichtung (Ue7)</h3>
        {data.origin === 'admin' && <Badge variant="info">angepasst am {dateShortDe(data.customized_at)}</Badge>}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {OFFER_SCORE_DIMENSION_VALUES.map((dim) => (
          <label key={dim} className="flex items-center justify-between gap-2 text-sm">
            <span>{OFFER_SCORE_DIMENSION_LABELS[dim]}</span>
            <input
              type="number"
              min={0}
              max={100}
              className="w-20 rounded-md border border-border p-1 text-right"
              value={draft[dim]}
              disabled={!canManage}
              onChange={(e) => set(dim, e.target.value)}
              aria-label={OFFER_SCORE_DIMENSION_LABELS[dim]}
            />
          </label>
        ))}
      </div>
      <div className={`mt-2 text-sm font-medium ${summe === 100 ? 'text-muted-foreground' : 'text-[var(--status-danger-text,#b42318)]'}`} data-testid="offer-score-gewichte-summe">
        Summe: {summe} %
      </div>
      {!gueltig && <p className="text-xs text-muted-foreground">{VERGLEICH_GEWICHTE_SUMME_HINWEIS}</p>}
      {canManage && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" disabled={!gueltig || put.isPending} onClick={() => put.mutate({ gewichte: draft })} data-testid="offer-score-gewichte-speichern">Speichern</Button>
          <Button variant="ghost" size="sm" disabled={reset.isPending} onClick={() => reset.mutate()} data-testid="offer-score-gewichte-reset">Auf Standard zuruecksetzen</Button>
        </div>
      )}
    </section>
  );
}
